// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// ✅ اصلاح: استفاده از مسیرهای نسبی به جای CDN خارجی
const PDF_JS_OPTIONS = {
  disableAutoFetch: true,
  disableStream: false,
  useWorkerFetch: true,
  useSystemFonts: true,
  cMapUrl: '/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/standard_fonts/',
  maxImageSize: 512 * 1024,
  enableXfa: false,
  stopAtErrors: false,
  fontExtraProperties: false,
  isEvalSupported: false,
  useStandardFontData: true,
  verbosity: 0,
};

const QUALITY_LEVELS = {
  low: { label: 'اقتصادی', dpi: 100 },
  medium: { label: 'متوسط', dpi: 150 },
  high: { label: 'بالا', dpi: 200 },
  ultra: { label: 'عالی', dpi: 300 },
};

const toPersian = (num) => {
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
  return String(num).replace(/\d/g, d => persianDigits[parseInt(d)]);
};

const PDFViewer = ({ mediaId, fileUrl, title, productTitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const stateData = location.state || {};
  const headerTitle = productTitle || stateData.productTitle || title || 'PDF';

  // ✅ لاگ برای بررسی mediaId
  console.log('📄 [PDFViewer] Received mediaId:', mediaId, 'fileUrl:', fileUrl);
  
  // ✅ اگر mediaId وجود نداشت، useSecureMedia را با undefined صدا نزن
  const shouldFetch = mediaId && !fileUrl;
  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } = 
    useSecureMedia(shouldFetch ? mediaId : undefined);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [isScannedPDF, setIsScannedPDF] = useState(null);
  const [quality, setQuality] = useState('high');
  const [imageScale, setImageScale] = useState(1.0);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);

  // ✅ اصلاح: استفاده از fileUrl یا mediaUrl با fallback
  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  // ✅ اصلاح: استفاده از mediaId واقعی (بدون fallback به 292)
  const effectiveMediaId = mediaId || undefined;

  // ✅ اصلاح: اگر هیچ منبعی وجود نداشت، خطا بده
  useEffect(() => {
    if (!proxyUrl && !effectiveMediaId) {
      setError('آدرس فایل معتبر نیست');
      setLoading(false);
      return;
    }
  }, [proxyUrl, effectiveMediaId]);

  // ✅ تشخیص اسکن بودن فایل
  useEffect(() => {
    if (!proxyUrl) return;
    if (!effectiveMediaId) {
      // اگر mediaId نداشتیم، از pdf.js استفاده کن
      setIsScannedPDF(false);
      setLoading(false);
      return;
    }
    
    isMounted.current = true;
    let cancelled = false;

    const checkSize = async () => {
      try {
        setLoading(true);
        // ✅ HEAD request با timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        let contentLength = 0;
        try {
          const headRes = await fetch(proxyUrl, { method: 'HEAD', signal: controller.signal });
          contentLength = parseInt(headRes.headers.get('Content-Length') || '0');
        } catch (headErr) {
          console.warn('HEAD request failed, using PDF.js fallback:', headErr.message);
        } finally {
          clearTimeout(timeoutId);
        }
        
        if (contentLength > 5 * 1024 * 1024 && effectiveMediaId) {
          setIsScannedPDF(true);
          const infoRes = await fetch(`/api/v1/products/pdf-info/${effectiveMediaId}/`);
          const info = await infoRes.json();
          if (!cancelled && isMounted.current) {
            setNumPages(info.numPages || 1);
            setLoading(false);
          }
        } else {
          setIsScannedPDF(false);
          // بارگذاری با pdf.js
          const loadingTask = pdfjsLib.getDocument({ url: proxyUrl, ...PDF_JS_OPTIONS });
          loadingTaskRef.current = loadingTask;
          const pdf = await loadingTask.promise;
          if (!cancelled && isMounted.current) {
            setPdfDoc(pdf);
            setNumPages(pdf.numPages);
            setLoading(false);
          }
        }
      } catch (err) {
        if (!cancelled && isMounted.current) {
          console.error('PDF load error:', err);
          setIsScannedPDF(false);
          setError('خطا در بارگذاری فایل: ' + err.message);
          setLoading(false);
        }
      }
    };

    checkSize();
    return () => {
      cancelled = true;
      isMounted.current = false;
      if (loadingTaskRef.current) {
        try { loadingTaskRef.current.destroy(); } catch (e) {}
        loadingTaskRef.current = null;
      }
    };
  }, [proxyUrl, effectiveMediaId]);

  // ✅ رندر PDF.js
  useEffect(() => {
    if (!pdfDoc || isScannedPDF || !canvasRef.current) return;
    let cancelled = false;
    
    (async () => {
      try {
        if (renderTaskRef.current) {
          try { await renderTaskRef.current.cancel(); } catch (e) {}
          renderTaskRef.current = null;
        }
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const cw = canvasRef.current.parentElement?.clientWidth || 800;
        const ch = window.innerHeight - 200;
        const s = Math.max(0.5, Math.min(cw / viewport.width, ch / viewport.height, 2));
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const vs = page.getViewport({ scale: s * dpr });
        const canvas = canvasRef.current;
        canvas.width = vs.width;
        canvas.height = vs.height;
        canvas.style.width = `${vs.width / dpr}px`;
        canvas.style.height = `${vs.height / dpr}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';
        const ctx = canvas.getContext('2d', { alpha: false });
        const renderTask = page.render({ canvasContext: ctx, viewport: vs, background: '#fff' });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        if (!cancelled) setScale(s);
      } catch (err) {
        if (err.name !== 'RenderingCancelledException' && err.name !== 'CancelException') {
          console.error('Render error:', err);
        }
      }
    })();
    
    return () => { cancelled = true; };
  }, [pdfDoc, pageNumber, isScannedPDF]);

  // ✅ اصلاح: استفاده از mediaId واقعی در getImageUrl
  const getImageUrl = useCallback((page, q = quality) => {
    if (!effectiveMediaId) {
      console.warn('⚠️ No mediaId for image URL');
      return null;
    }
    return `/api/v1/products/pdf-to-image/${effectiveMediaId}/?page=${page}&quality=${q}`;
  }, [effectiveMediaId, quality]);

  const changePage = (offset) => setPageNumber(prev => Math.max(1, Math.min(prev + offset, numPages)));
  const goToPage = (page) => setPageNumber(Math.max(1, Math.min(page, numPages)));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const imageZoomIn = () => setImageScale(prev => Math.min(prev + 0.5, 4.0));
  const imageZoomOut = () => setImageScale(prev => Math.max(prev - 0.5, 0.5));
  
  // ✅ اصلاح: ری‌ست زوم هنگام تغییر کیفیت
  const cycleQuality = () => {
    const qualities = Object.keys(QUALITY_LEVELS);
    setQuality(qualities[(qualities.indexOf(quality) + 1) % qualities.length]);
    setImageScale(1.0);
  };

  const handleContextMenu = (e) => e.preventDefault();

  // ✅ زوم تصویر با transform
  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.style.transform = `scale(${imageScale})`;
    }
  }, [imageScale]);

  // ✅ کیبورد
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) e.preventDefault();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); changePage(1); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); changePage(-1); }
      if (isScannedPDF && (e.key === '+' || e.key === '=')) { e.preventDefault(); imageZoomIn(); }
      if (isScannedPDF && e.key === '-') { e.preventDefault(); imageZoomOut(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [changePage, isScannedPDF]);

  // ✅ cleanup
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (e) {}
        renderTaskRef.current = null;
      }
    };
  }, []);

  // ✅ نمایش خطاها
  if (urlError || error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8" style={{ fontFamily: 'Vazirmatn' }}>
        <p className="text-red-500 mb-4">{error || urlError}</p>
        <button 
          onClick={() => { setError(null); refetchMediaUrl?.(); }} 
          className="px-4 py-2 bg-teal-600 text-white rounded-lg"
        >
          تلاش مجدد
        </button>
      </div>
    );
  }

  if (isScannedPDF === null || loading || urlLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center" style={{ fontFamily: 'Vazirmatn' }}>
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const HeaderBar = () => (
    <div className="bg-white border-b-2 border-teal-700 shadow-sm px-3 py-3 flex items-center gap-2" style={{ fontFamily: 'Vazirmatn' }}>
      <button onClick={() => navigate(-1)} className="text-teal-700 text-xl flex-shrink-0">←</button>
      <h2 className="text-teal-800 font-bold text-sm flex-1 text-right leading-6" style={{ wordBreak: 'break-word' }}>{headerTitle}</h2>
      <span className="text-teal-600 text-xs flex-shrink-0">{toPersian(pageNumber)} / {toPersian(numPages)}</span>
    </div>
  );

  // ==================== PDF.js Render ====================
  if (!isScannedPDF) {
    return (
      <div className="h-screen flex flex-col bg-gray-100 select-none" onContextMenu={handleContextMenu} dir="ltr">
        <HeaderBar />
        <div className="bg-white border-b px-4 py-2 flex items-center justify-center gap-2" style={{ fontFamily: 'Vazirmatn' }}>
          <button onClick={zoomOut} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">−</button>
          <span className="text-xs text-teal-700 w-10 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">+</button>
          <div className="w-px h-5 bg-gray-300 mx-1" />
          <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">◀</button>
          <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-10 h-8 text-center border rounded text-xs text-teal-700" style={{ fontFamily: 'Vazirmatn' }} />
          <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">▶</button>
        </div>
        <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-2 bg-gray-50">
          <canvas ref={canvasRef} />
        </div>
      </div>
    );
  }

  // ==================== Scanned PDF (Image Mode) ====================
  const imageUrl = getImageUrl(pageNumber, quality);

  return (
    <div className="h-screen flex flex-col bg-gray-50 select-none" onContextMenu={handleContextMenu} dir="ltr">
      <HeaderBar />
      <div className="bg-white border-b px-4 py-2 flex items-center justify-center gap-2 flex-wrap" style={{ fontFamily: 'Vazirmatn' }}>
        <button onClick={imageZoomOut} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">−</button>
        <span className="text-xs text-teal-700 w-10 text-center">{Math.round(imageScale * 100)}%</span>
        <button onClick={imageZoomIn} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">+</button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button onClick={cycleQuality} className="px-2 py-1 text-xs rounded bg-gray-100 text-teal-700">{QUALITY_LEVELS[quality]?.label}</button>
        <span className="text-xs text-gray-400">{QUALITY_LEVELS[quality]?.dpi} DPI</span>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">◀</button>
        <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-10 h-8 text-center border rounded text-xs text-teal-700" style={{ fontFamily: 'Vazirmatn' }} />
        <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">▶</button>
      </div>
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100 p-2">
        {imageUrl ? (
          <img 
            ref={imgRef}
            key={`${pageNumber}-${quality}`}
            src={imageUrl} 
            alt={`صفحه ${pageNumber}`} 
            style={{ 
              width: '100%', 
              height: 'auto', 
              display: 'block', 
              margin: '0 auto',
              transform: `scale(${imageScale})`,
              transformOrigin: 'center',
              transition: 'transform 0.2s ease'
            }}
            crossOrigin="use-credentials"
            onError={() => setError('خطا در بارگذاری تصویر')} 
          />
        ) : (
          <div className="text-center text-gray-500 p-8">تصویر در دسترس نیست</div>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;